import { useAtom, useAtomValue } from 'jotai';
import { selectedEventAtom, selectedMarketplaceEventAtom, showEventMappingAtom } from '../../data/atoms';
import { useCallback, useMemo } from 'react';
import { DatesRangeValue } from '@mantine/dates';
import { useForm } from '@mantine/form';
import {
  BrokerNerdsBarkerCoreEnumsMarketplace,
  BrokerNerdsBarkerCoreModelsInventoryEvent,
  BrokerNerdsBarkerCoreModelsInventoryEventMappingSuccess,
  getGetApiInventoryEventsEventIdMappingsQueryKey,
  getGetApiMarketplacesMarketplaceIdEventsQueryKey,
  useDeleteApiInventoryEventsEventIdMappingsMarketplaceId,
  useGetApiMarketplacesMarketplaceIdEvents,
  useGetApiMarketplacesMarketplaceIdEventsEventId,
  usePostApiInventoryEventsEventIdMappings,
} from '../../api';
import dayjs from 'dayjs';
import { parseEventIdFromUrl } from '../../utils/urls';
import yasml from '@thirtytech/yasml';
import { useDidUpdate } from '@mantine/hooks';
import { queryClient } from '../../data/api-config';

function EventMappingState({ overrideEvent }: { overrideEvent?: BrokerNerdsBarkerCoreModelsInventoryEvent | null }) {
  const [showEventMapping, setShowEventMapping] = useAtom(showEventMappingAtom);
  const selectedMarketplaceEvent = useAtomValue(selectedMarketplaceEventAtom);
  const selectedEventGlobal = useAtomValue(selectedEventAtom);
  const selectedEvent = overrideEvent || selectedEventGlobal;
  // const { refetchEventMappings } = useSeatingChart('refetchEventMappings');

  const fixedStartDate = 'T00:00:00.000';
  const fixedEndOfDate = 'T23:59:59.000';

  const searchForm = useForm<{ dateRange: DatesRangeValue; searchTerm: string }>({
    initialValues: {
      dateRange: [
        dayjs(selectedEvent?.localDateTime ?? new Date())
          .startOf('day')
          .toDate(),
        dayjs(selectedEvent?.localDateTime ?? new Date())
          .startOf('day')
          .toDate(),
      ],
      searchTerm: selectedEvent?.name ?? '',
    },
    validate: {
      searchTerm: (value) => (value.length < 1 ? 'Search field is required' : null),
    },
  });

  useDidUpdate(() => {
    // Reset initial state of the form when selectedEvent changes needed for season view
    searchForm.setValues({
      dateRange: [
        dayjs(selectedEvent?.localDateTime ?? new Date())
          .startOf('day')
          .toDate(),
        dayjs(selectedEvent?.localDateTime ?? new Date())
          .startOf('day')
          .toDate(),
      ],
      searchTerm: selectedEvent?.name ?? '',
    });
  }, [selectedEvent]);

  const pasteUrlForm = useForm<{ url: string }>({
    initialValues: {
      url: '',
    },
    validate: {
      url: (value) => (parseEventIdFromUrl(value) === null ? 'Invalid URL' : null),
    },
  });

  // Memoize the dayjs parse/format work; pure function of dateRange so the produced strings are identical, and React Query compares query keys/params by value (not reference).
  const dateRange = searchForm.values.dateRange;
  const startDate = useMemo(() => (dayjs(dateRange[0]).format('YYYY-MM-DD') + fixedStartDate) as unknown as Date, [dateRange, fixedStartDate]);
  const endDate = useMemo(() => (dayjs(dateRange[1]).endOf('day').format('YYYY-MM-DD') + fixedEndOfDate) as unknown as Date, [dateRange, fixedEndOfDate]);

  const {
    data: searchResults,
    refetch: searchEvents,
    isFetching: isSearchLoading,
  } = useGetApiMarketplacesMarketplaceIdEvents(
    selectedMarketplaceEvent?.marketplaceId!,
    {
      query: searchForm.values.searchTerm,
      fromDate: startDate,
      toDate: endDate,
    },
    {
      query: {
        select(data) {
          return data.data;
        },
        enabled: false,
      },
      axios: {
        headers: {
          'x-tenant-id': selectedEvent?.tenantId,
        },
      },
    },
  );

  function clearSearchResults() {
    queryClient.removeQueries({
      queryKey: getGetApiMarketplacesMarketplaceIdEventsQueryKey(selectedMarketplaceEvent?.marketplaceId!, {
        query: searchForm.values.searchTerm,
        fromDate: startDate,
        toDate: endDate,
      }),
    });
  }

  const { mutateAsync: unlinkEvent, isPending: isUnlinkingEvent } = useDeleteApiInventoryEventsEventIdMappingsMarketplaceId({
    axios: {
      headers: {
        'x-tenant-id': selectedEvent?.tenantId,
      },
    },
  });

  const { mutateAsync: linkEvent, isPending: isLinkingEvent } = usePostApiInventoryEventsEventIdMappings({
    axios: {
      headers: {
        'x-tenant-id': selectedEvent?.tenantId,
      },
    },
  });

  const removeEventLink = useCallback(
    async (marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace, eventId: string) => {
      await unlinkEvent({ marketplaceId, eventId });
      await queryClient.invalidateQueries({ queryKey: getGetApiInventoryEventsEventIdMappingsQueryKey(eventId) });
    },
    [unlinkEvent],
  );

  const updateEventLink = useCallback(
    async (marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace, eventId: string, data: BrokerNerdsBarkerCoreModelsInventoryEventMappingSuccess) => {
      await unlinkEvent({ marketplaceId, eventId });
      await linkEvent({ eventId, data });
      await queryClient.invalidateQueries({ queryKey: getGetApiInventoryEventsEventIdMappingsQueryKey(eventId) });
    },
    [unlinkEvent, linkEvent],
  );

  // A primitive boolean has no referential identity to preserve, so memoizing a trivial `||` only adds deps-array bookkeeping overhead.
  const isLinking = isLinkingEvent || isUnlinkingEvent;

  const {
    data: pastedEvent,
    refetch: fetchPastedEvent,
    isFetching: isPastedEventLoading,
  } = useGetApiMarketplacesMarketplaceIdEventsEventId(selectedMarketplaceEvent?.marketplaceId!, parseEventIdFromUrl(pasteUrlForm.values.url) ?? '', {
    query: {
      select(data) {
        return data.data;
      },
      enabled: false,
    },
    axios: {
      headers: {
        'x-tenant-id': selectedEvent?.tenantId,
      },
    },
  });

  return {
    showEventMapping,
    setShowEventMapping,
    selectedMarketplaceEvent,
    selectedEvent,
    searchForm,
    searchEvents,
    searchResults,
    isSearchLoading,
    pasteUrlForm,
    pastedEvent,
    fetchPastedEvent,
    isPastedEventLoading,
    unlinkEvent: removeEventLink,
    isUnlinkingEvent,
    linkEvent: updateEventLink,
    isLinkingEvent: isLinking,
    clearSearchResults,
  };
}

export const { Provider: EventMappingProvider, useSelector: useEventMapping } = yasml(EventMappingState);
